import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { NotificationsService } from '../../services/notifications.service';
import { takeUntil } from 'rxjs';
import { PaginatedResponse } from '../../../../shared/interfaces/pagination';
import { Notifications, NotificationTypes } from '../../models/notifications';
import { formatNotification } from '../../utils/notification-formatter';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { FormsModule } from '@angular/forms';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { TimeAgoPipe } from '../../../../shared/pipes/time-ago.pipe';
import { Router } from '@angular/router';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { RecordDetailsComponent } from '../../../../shared/components/record-details/record-details.component';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';
import { AuthService } from '../../../auth/services/auth.service';
import { OutreachService } from '../../../outreach/services/outreach.service';
import { NotificationSettingsComponent } from '../notification-settings/notification-settings.component';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { AnnouncementDetailComponent } from '../../../announcements/components/announcement-details/announcement-details.component';
import { ToastType } from '../../../../shared/interfaces/common';
import { CommonService } from '../../../../shared/services/common/common.service';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { DatePipe } from '@angular/common';
import {
  EntityType,
  ReportContentData,
} from '../../../../shared/interfaces/report-content';
import { ReportContentService } from '../../../../shared/services/reports/report-content.service';
@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [
    PageSizeComponent,
    SvgIconComponent,
    FormsModule,
    PaginationComponent,
    TimeAgoPipe,
    PopupComponent,
    RecordDetailsComponent,
    NotificationSettingsComponent,
    AnnouncementDetailComponent,
    SecondaryPopupComponent,
    SkeletonComponent,
    DatePipe,
  ],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
})
export class NotificationsComponent
  extends MenuComponentBase
  implements OnInit
{
  notificationsService = inject(NotificationsService);
  router = inject(Router);
  authService = inject(AuthService);
  outreachService = inject(OutreachService);
  private commonService = inject(CommonService);
  private reportContentService = inject(ReportContentService);

  onReportNotification(notification: Notifications, event: Event): void {
    event.stopPropagation();
    const payload: ReportContentData = {
      entityId: notification.id,
      entityType: EntityType.NOTIFICATION,
      title: 'Flag Notification',
      itemTitle: notification?.title || notification?.body || '',
    };
    this.reportContentService.open(payload);
  }

  loading = false;
  currentPage = 1;
  pageSize = 50;
  totalNotifications = 0;
  notifications: Notifications[] = [];
  hasUnreadNotification = false;
  selectedNotifications: Notifications[] = [];
  allChecked = false;
  showCDFIOutreachAcceptRejectPopup = false;
  clickedNotification!: Notifications;
  isAcceptedCDFIOutreach = false;
  isAdminUser = this.authService.hasPermission(PermissionCode.ADMIN_PRIVILEGES);
  showRecordDetails = false;
  showAnnouncementPopup = false;
  uniqueId!: string;
  showNotes = false;
  private lastKnownUnreadIds: number[] = [];
  announcementId!: number | undefined;

  showBusinessDeletedPopup = false;
  selectedDeletedBusinessName = '';
  selectedDeletedBusinessTime = '';

  // Notification settings properties
  showNotificationSettings = false;
  @ViewChild(NotificationSettingsComponent)
  notificationSettingsComponent!: NotificationSettingsComponent;
  readonly isSmeUser = this.authService.isSMEUser();

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadNotifications(this.currentPage, this.pageSize);

    this.notificationsService.hasUnreadNotificationExists$
      .pipe(takeUntil(this.destroy$))
      .subscribe((newUnreadIds) => {
        // Only reload if there are genuinely new notification IDs that we haven't seen before
        const hasNewNotification = newUnreadIds.some(
          (notificationId) => !this.lastKnownUnreadIds.includes(notificationId),
        );

        if (hasNewNotification && this.lastKnownUnreadIds.length > 0) {
          this.loadNotifications(this.currentPage, this.pageSize);
        }

        // Update our tracking of known unread IDs
        this.lastKnownUnreadIds = [...newUnreadIds];
      });
  }

  loadNotifications(page: number, pageSize: number): void {
    this.loading = true;
    // Preserve currently selected notification IDs
    const selectedNotificationIds = this.selectedNotifications.map((n) => n.id);

    this.notificationsService
      .getAllNotifications(page, pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: PaginatedResponse<Notifications>) => {
        this.totalNotifications = response.count;
        this.notifications = response.results.map((notification) => {
          const formatted = formatNotification(notification);
          notification.title = formatted.title;
          notification.body = formatted.body;
          notification.message = formatted.body;
          notification.action_label = formatted.actionLabel;
          if (notification.notification_content.expires_in_days) {
            const remainingDays = this.calculateRemainingDays(notification);
            notification.expiry_text =
              remainingDays === 0
                ? 'Expires today'
                : `Expires in ${remainingDays} days`;
          }
          return notification;
        });

        // Restore selection state for notifications that are still present
        this.selectedNotifications = this.notifications.filter((notification) =>
          selectedNotificationIds.includes(notification.id),
        );

        // Update allChecked state based on remaining selections
        this.allChecked =
          this.notifications.length > 0 &&
          this.selectedNotifications.length === this.notifications.length;

        this.hasUnreadNotification = this.notifications.some(
          (item) => !item.is_read,
        );
        this.loading = false;
      });
  }

  private calculateRemainingDays(notification: Notifications): number {
    const createdDate = new Date(notification.created_on_utc);
    const totalExpiryDays =
      notification.notification_content.expires_in_days || 0;
    const today = new Date();

    // Count only business days (Mon–Fri) elapsed since creation
    let businessDaysPassed = 0;
    const cursor = new Date(createdDate);
    cursor.setDate(cursor.getDate() + 1); // start counting the day after creation
    while (cursor <= today) {
      const dow = cursor.getDay(); // 0=Sun, 6=Sat
      if (dow !== 0 && dow !== 6) {
        businessDaysPassed++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return Math.max(0, totalExpiryDays - businessDaysPassed);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadNotifications(this.currentPage, this.pageSize);
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadNotifications(this.currentPage, this.pageSize);
    }
  }

  onNotificationChecked(notification: Notifications, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedNotifications.push(notification);
    } else {
      this.selectedNotifications = this.selectedNotifications.filter(
        (item) => item !== notification,
      );
    }
  }

  onAllCheckBoxChange(): void {
    if (this.allChecked) {
      this.selectedNotifications = this.notifications;
    } else {
      this.selectedNotifications = [];
    }
  }

  clearSelectedNotifications(): void {
    const Ids = this.selectedNotifications.map((item) => item.id);
    this.loading = true;
    this.notificationsService
      .deleteNotification(Ids)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.notifications = this.notifications.filter(
          (n) => !Ids.includes(n.id),
        );
        this.selectedNotifications = [];
        this.allChecked = false;

        const totalRemainingNotifications =
          this.totalNotifications - Ids.length;
        const maxPage = Math.max(
          1,
          Math.ceil(totalRemainingNotifications / this.pageSize),
        );

        this.currentPage = Math.min(this.currentPage, maxPage);
        this.loadNotifications(this.currentPage, this.pageSize);
      });
  }

  private markNotificationAsRead(
    notification: Notifications,
    callback: () => void,
  ): void {
    if (!notification.is_read) {
      this.notificationsService
        .updateNotificationReadStatus([notification.id], true)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          notification.is_read = true;
          this.hasUnreadNotification = this.notifications.some(
            (item) => !item.is_read,
          );
          callback();
        });
    } else {
      callback();
    }
  }

  markSelectedAsRead(): void {
    this.updateSelectedNotificationsReadStatus(true);
  }

  markSelectedAsUnread(): void {
    this.updateSelectedNotificationsReadStatus(false);
  }

  private updateSelectedNotificationsReadStatus(isRead: boolean): void {
    const targetNotifications = this.selectedNotifications.filter(
      (notification) => notification.is_read !== isRead,
    );

    if (targetNotifications.length === 0) {
      return; // No notifications to update
    }

    const ids = targetNotifications.map((item) => item.id);
    this.notificationsService
      .updateNotificationReadStatus(ids, isRead)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Update the is_read status for the selected notifications
        targetNotifications.forEach((notification) => {
          notification.is_read = isRead;
        });

        // Update hasUnreadNotification status
        this.hasUnreadNotification = this.notifications.some(
          (item) => !item.is_read,
        );

        // Clear selection after updating status
        this.selectedNotifications = [];
        this.allChecked = false;
      });
  }

  get hasOnlyUnreadSelected(): boolean {
    return (
      this.selectedNotifications.length > 0 &&
      this.selectedNotifications.every((notification) => !notification.is_read)
    );
  }

  get hasOnlyReadSelected(): boolean {
    return (
      this.selectedNotifications.length > 0 &&
      this.selectedNotifications.every((notification) => notification.is_read)
    );
  }

  get hasMixedReadStatusSelected(): boolean {
    if (this.selectedNotifications.length <= 1) return false;

    const hasRead = this.selectedNotifications.some(
      (notification) => notification.is_read,
    );
    const hasUnread = this.selectedNotifications.some(
      (notification) => !notification.is_read,
    );

    return hasRead && hasUnread;
  }

  onNotificationOpen(notification: Notifications): void {
    this.showNotes = false;
    if (
      notification.notification_type === NotificationTypes.SHARE_RECORD_NOTE
    ) {
      this.showNotes = true;
    }

    if (
      notification.notification_type === NotificationTypes.LEAD_BUSINESS_DELETED
    ) {
      this.markNotificationAsRead(notification, () => {
        this.selectedDeletedBusinessName =
          notification.notification_content.business_name || '';
        this.selectedDeletedBusinessTime = notification.created_on_utc;
        this.showBusinessDeletedPopup = true;
      });
      return;
    }

    if (notification.notification_type === NotificationTypes.ORG_SELF_SIGNUP) {
      this.markNotificationAsRead(notification, () => {
        this.router.navigate(['/connection-signup-requests'], {
          queryParams: {
            application: notification.notification_content.application_id,
          },
        });
      });
      return;
    }

    // Handle SHARE_DOCUMENT notification type
    if (
      notification.notification_type ===
        NotificationTypes.SHARE_DOCUMENT_TO_USER ||
      notification.notification_type ===
        NotificationTypes.SHARE_DOCUMENT_TO_CONNECTION
    ) {
      this.markNotificationAsRead(notification, () => {
        this.router.navigate(['/files'], {
          queryParams: {
            shared: 'true',
          },
        });
      });
      return;
    } else if (
      notification.notification_type === NotificationTypes.PDM_SHARE_APPROVAL
    ) {
      this.markNotificationAsRead(notification, () => {
        this.router.navigate(['/files'], {
          queryParams: {
            pdm_only: 'true',
          },
        });
      });
      return;
    }

    // Handle ANNOUNCEMENT - mark as read and show announcement popup
    if (notification.notification_type === NotificationTypes.ANNOUNCEMENT) {
      this.markNotificationAsRead(notification, () => {
        const announcementId =
          notification.notification_content.announcement_id;
        if (announcementId) {
          this.announcementId = announcementId;
          this.showAnnouncementPopup = true;
        } else {
          this.showToastMessage('Something went wrong', 'error');
        }
      });
      return;
    }

    // Handle USER_PROFILE_VIEW - mark as read and navigate to cdfi profile details
    if (
      notification.notification_type === NotificationTypes.USER_PROFILE_VIEW
    ) {
      this.markNotificationAsRead(notification, () => {
        this.router.navigate([
          '/cdfi/profile/' +
            notification.notification_content.viewed_by_user_connection_name,
        ]);
      });
      return;
    }

    // Handle CDFI_PROFILE_VIEW - mark as read and navigate to user profile details
    if (
      notification.notification_type === NotificationTypes.CDFI_PROFILE_VIEW
    ) {
      this.markNotificationAsRead(notification, () => {
        this.router.navigate(
          [`/sme/${notification.notification_content.viewed_by_user}/profile/`],
          {
            state: { fromNotifications: true },
          },
        );
      });
      return;
    }

    // FILE_MALICIOUS_DETECTED - redirect to files page
    if (
      notification.notification_type ==
      NotificationTypes.FILE_MALICIOUS_DETECTED
    ) {
      this.markNotificationAsRead(notification, () => {
        this.router.navigate(['/files']);
      });
      return;
    }

    // SME_LEAD_ASSIGNED / SME_LEAD_STATUS_CHANGED - redirect to business page
    if (
      notification.notification_type === NotificationTypes.SME_LEAD_ASSIGNED ||
      notification.notification_type ===
        NotificationTypes.SME_LEAD_STATUS_CHANGED
    ) {
      this.markNotificationAsRead(notification, () => {
        this.router.navigate(['/business']);
      });
      return;
    }

    // Handle all other notification types - mark as read and show record details
    this.markNotificationAsRead(notification, () => {
      this.uniqueId = notification.notification_content.record_unique_id!;
      this.showRecordDetails = true;
    });
  }

  onCDFIOutreachAcceptReject(
    notification: Notifications,
    status: boolean,
  ): void {
    this.clickedNotification = notification;
    this.isAcceptedCDFIOutreach = status;
    this.showCDFIOutreachAcceptRejectPopup = true;
  }

  updateOutreachStatus(): void {
    this.outreachService
      .updateOutreachStatus(
        this.clickedNotification.notification_content.outreach_form_id!,
        this.isAcceptedCDFIOutreach,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.showCDFIOutreachAcceptRejectPopup = false;
        this.clickedNotification.is_read = true;
        this.loadNotifications(this.currentPage, this.pageSize);
      });
  }

  openNotificationSettings(): void {
    this.showNotificationSettings = true;
  }

  closeNotificationSettings(): void {
    this.showNotificationSettings = false;
  }

  private showToastMessage(message: string, type: 'success' | 'error'): void {
    this.commonService.triggerToast({
      type: type === 'success' ? ToastType.Success : ToastType.Error,
      message: message,
    });
  }

  onAnnouncementPopupClose(): void {
    this.showAnnouncementPopup = false;
    this.announcementId = undefined;
  }

  onSettingsSaved(result: {
    success: boolean;
    message: string;
    keepOpen?: boolean;
  }): void {
    this.showToastMessage(result.message, result.success ? 'success' : 'error');
    if (!result.keepOpen) {
      this.closeNotificationSettings();
    }
  }

  saveNotificationSettings(): void {
    // Call the save method on the child component
    if (this.notificationSettingsComponent) {
      this.notificationSettingsComponent.saveSettings();
    }
  }
}
